import React from 'react';
import { MdDone, MdRemove } from 'react-icons/md';
import styled from 'styled-components';

import { ColorVariantEnum, MarginSizeEnum, Text } from '../../atoms/text';
import { theme } from '../../theme';

interface IStyledCheckbox {
	isChecked: boolean;
	isIndeterminate?: boolean;
	isDisabled?: boolean;
	text?: string;
}

interface ICheckbox extends IStyledCheckbox {
	name?: string;
	onClick: (e: React.MouseEvent<HTMLInputElement>) => void;
}

interface IStyledLabel extends IStyledCheckbox {}

const getBorderColor = (props: IStyledCheckbox, isHover?: boolean) => {
	if (props.isDisabled) {
		return theme.colors.checkbox.border.disabled;
	}
	if (props.isChecked || props.isIndeterminate) {
		return theme.colors.checkbox.border.checked;
	}
	if (isHover) {
		return theme.colors.checkbox.border.hover;
	}
	return theme.colors.checkbox.border.default;
};

const getBackgroundColor = (props: IStyledCheckbox) => {
	if (props.isDisabled && (props.isChecked || props.isIndeterminate)) {
		return theme.colors.checkbox.background.disabled;
	}
	if (props.isChecked || props.isIndeterminate) {
		return theme.colors.checkbox.background.checked;
	}
	return theme.colors.checkbox.background.default;
};

const StyledLabel = styled.label<IStyledLabel>`
	display: inline-flex;
	align-items: flex-start;
	cursor: ${(props) => (props.isDisabled ? 'default' : 'pointer')};
	:hover {
		.styled-checkbox {
			border-color: ${(props) => getBorderColor(props, true)};
		}
	}
`;

const StyledCheckbox = styled.div<IStyledCheckbox>`
	position: relative;
	width: 18px;
	height: 18px;
	min-width: 18px;
	min-height: 18px;
	margin-right: 10px;
	margin-top: ${({ text }) => (text ? '3px' : 0)};
	cursor: ${(props) => (props.isDisabled ? 'default' : 'pointer')};
	border: ${(props) => `2px solid ${getBorderColor(props)}`};
	border-radius: 3px;
	background-color: ${(props) => getBackgroundColor(props)};
	:hover {
		border-color: ${theme.colors.checkbox.border.hover};
	}
`;

const StyledInput = styled.input`
	opacity: 0;
	width: 0;
	height: 0;
`;

const StyledCheckedIcon = styled(MdDone)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	stroke-width: 2;
`;

const StyledIndeterminateIcon = styled(MdRemove)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	stroke-width: 2;
`;

export const Checkbox = ({ text, name, isChecked, isDisabled, isIndeterminate, onClick }: ICheckbox) => {
	return (
		<StyledLabel isChecked={isChecked} isDisabled={isDisabled} isIndeterminate={isIndeterminate}>
			<StyledInput type="checkbox" name={name} checked={isChecked} onClick={onClick} />
			<StyledCheckbox
				isChecked={isChecked}
				isDisabled={isDisabled}
				isIndeterminate={isIndeterminate}
				className="styled-checkbox"
				text={text}
			>
				{isChecked && !isIndeterminate && <StyledCheckedIcon color={theme.colors.checkbox.icon} size={16} />}
				{isIndeterminate && !isChecked && <StyledIndeterminateIcon color={theme.colors.checkbox.icon} size={16} />}
			</StyledCheckbox>
			<Text.Paragraph
				colorVariant={
					isDisabled
						? ColorVariantEnum.DISABLED
						: isChecked || isIndeterminate
						? ColorVariantEnum.DARK
						: ColorVariantEnum.MEDIUM
				}
				margin={MarginSizeEnum.NONE}
			>
				{text}
			</Text.Paragraph>
		</StyledLabel>
	);
};
